import type { SettingsOption } from './types';

export interface FastFillField {
  key: string;
  label: string;
  dataName: string;
  type: string;
  displayType: string;
  description?: string;
  repeatableContainerKey?: string;
  disabled?: boolean;
}

export type FastFillSavedType = 'generic' | 'text_extraction';

export type FastFillEffectiveType = FastFillSavedType | 'off';

export type FastFillLegacyPromptTarget =
  | { kind: 'field'; key: string }
  | { kind: 'unresolved'; dataName: string };

export interface FastFillConfiguration {
  id: string;
  sourceKey: string;
  targetFieldKeys: string[];
  legacyTargetDataNames?: string[];
  legacyPromptOrder?: FastFillLegacyPromptTarget[];
  type: FastFillSavedType;
  isExisting: boolean;
  existingSourceKey?: string;
}

export interface FastFillSettingsDraft {
  audioFastFillEnabled: boolean;
  configurations: FastFillConfiguration[];
}

export interface FastFillSettingsEnvironment {
  sourceFields: FastFillField[];
  targetFields: FastFillField[];
  inactiveTargetFields: FastFillField[];
  fastfillEnabled: boolean;
  photoPlanEnabled: boolean;
  canManageSubscription: boolean;
  canUpdateOrganization: boolean;
  audioFastFillVisible: boolean;
  audioFastFillDisabled: boolean;
}

export interface FastFillTargetSelection {
  configurationId: string;
  selectedTargetFieldKeys: string[];
  selectedLegacyTargetDataNames?: string[];
  legacyPromptOrder?: FastFillLegacyPromptTarget[];
}

export interface FastFillTargetFieldsEnvironment {
  sourceField?: FastFillField;
  eligibleTargetFields: FastFillField[];
  inactiveSelectedTargetFields: FastFillField[];
  unresolvedTargetFieldKeys: string[];
  unresolvedLegacyTargetDataNames?: string[];
}

interface LegacyElementModel {
  get?: (key: string) => unknown;
  set?: (key: string | Record<string, unknown>, value?: unknown) => unknown;
  unset?: (key: string) => unknown;
  repeatableContainer?: () => LegacyElementModel | null;
}

const PHOTO_FIELD_TYPE = 'PhotoField';
const TARGET_FIELD_BLACKLIST = new Set([
  'Repeatable',
  'Section',
  'PhotoField',
  'VideoField',
  'AudioField',
  'Label',
  'CalculatedField',
  'SignatureField',
  'AttachmentField',
  'SketchField',
  'RecordLinkField',
]);

const readString = (value: unknown): string => (typeof value === 'string' ? value : '');

const hiddenFieldIsTrue = (id: string): boolean => {
  const field = document.getElementById(id);
  return field instanceof HTMLInputElement && field.value === 'true';
};

const legacyTargetDataNamesFromPrompt = (value: unknown): string[] => (
  readString(value)
    .split('\n')
    .map((dataName) => dataName.trim())
    .filter(Boolean)
);

const fieldTypeLabel = (type: string, numeric: boolean): string => {
  if (type === 'TextField') return numeric ? 'Numeric' : 'Text';
  if (type === 'ChoiceField') return 'Choice';
  if (type === 'YesNoField') return 'Yes/No';

  return type.endsWith('Field') ? type.slice(0, -'Field'.length) : type;
};

const isLegacyElementModel = (value: unknown): value is LegacyElementModel => {
  if (typeof value !== 'object' || value === null) return false;

  const model = value as {
    get?: unknown;
    set?: unknown;
    repeatableContainer?: unknown;
  };
  return (
    (typeof model.get === 'function' || typeof model.set === 'function')
    && (model.get === undefined || typeof model.get === 'function')
    && (model.set === undefined || typeof model.set === 'function')
    && (model.repeatableContainer === undefined || typeof model.repeatableContainer === 'function')
  );
};

const fieldFromModel = (model: LegacyElementModel): FastFillField | null => {
  const key = readString(model.get?.('key'));
  const dataName = readString(model.get?.('data_name'));
  const type = readString(model.get?.('type'));
  const description = readString(model.get?.('description'));
  const repeatableContainerKey = readString(model.repeatableContainer?.()?.get?.('key'));
  const disabled = model.get?.('disabled') === true || model.get?.('enabled') === false;
  if (!key || !dataName) return null;

  return {
    key,
    dataName,
    label: readString(model.get?.('label')) || dataName,
    type,
    displayType: fieldTypeLabel(type, model.get?.('numeric') === true),
    ...(description ? { description } : {}),
    ...(repeatableContainerKey ? { repeatableContainerKey } : {}),
    ...(disabled ? { disabled: true } : {}),
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const isSavedType = (value: unknown): value is FastFillSavedType => (
  value === 'generic' || value === 'text_extraction'
);

const uniqueNonEmptyStrings = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];

  return [...new Set(
    value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0),
  )];
};

interface PersistedFastFillSettings {
  target_fields: string[];
  type: FastFillSavedType;
}

const isValidTargetFields = (value: unknown): value is string[] => (
  Array.isArray(value)
  && value.every((targetField) => (
    typeof targetField === 'string' && targetField.trim().length > 0
  ))
  && new Set(value).size === value.length
);

const hasNoNormalizedTargets = (value: unknown): boolean => (
  isRecord(value)
  && Array.isArray(value.target_fields)
  && uniqueNonEmptyStrings(value.target_fields).length === 0
);

const isValidPersistedFastFillSettings = (
  value: unknown,
): value is PersistedFastFillSettings => isRecord(value)
  && isValidTargetFields(value.target_fields)
  && value.target_fields.length > 0
  && isSavedType(value.type);

const fastFillSettingsFromModel = (model: LegacyElementModel): {
  targetFieldKeys: string[];
  type: FastFillSavedType;
} | null => {
  const settings = model.get?.('fastfill_settings');
  if (!isRecord(settings) || !isValidTargetFields(settings.target_fields)) return null;

  return {
    targetFieldKeys: [...settings.target_fields],
    type: isSavedType(settings.type) ? settings.type : 'generic',
  };
};

const models = (): LegacyElementModel[] => (
  (window.FormBuilder?.model?.allElements?.() ?? []).filter(isLegacyElementModel)
);

export const readFastFillSettings = (): FastFillSettingsDraft => {
  const elementModels = models();
  const fieldKeysByDataName = new Map<string, string[]>();
  elementModels
    .map(fieldFromModel)
    .filter((field): field is FastFillField => field !== null)
    .forEach(({ dataName, key }) => {
      fieldKeysByDataName.set(dataName, [...(fieldKeysByDataName.get(dataName) ?? []), key]);
    });
  const configurations = elementModels
    .filter((model) => readString(model.get?.('type')) === PHOTO_FIELD_TYPE)
    .map((model): FastFillConfiguration | null => {
      const sourceKey = readString(model.get?.('key'));
      const settings = fastFillSettingsFromModel(model);
      const legacyTargetDataNames = legacyTargetDataNamesFromPrompt(model.get?.('ai_prompt'));
      if (!sourceKey || (!settings && legacyTargetDataNames.length === 0)) return null;

      const legacyPromptOrder = legacyTargetDataNames.map((dataName) => {
        const matchingKeys = fieldKeysByDataName.get(dataName) ?? [];
        return matchingKeys.length === 1
          ? { kind: 'field' as const, key: matchingKeys[0] }
          : { kind: 'unresolved' as const, dataName };
      });
      const legacyTargetFieldKeys = legacyPromptOrder
        .filter((target): target is Extract<FastFillLegacyPromptTarget, { kind: 'field' }> => (
          target.kind === 'field'
        ))
        .map(({ key }) => key);
      const unresolvedLegacyTargetDataNames = legacyTargetDataNames
        .filter((dataName) => (fieldKeysByDataName.get(dataName) ?? []).length !== 1);
      const targetFieldKeys = uniqueNonEmptyStrings([
        ...legacyTargetFieldKeys,
        ...(settings?.targetFieldKeys ?? []),
      ]);
      const orderedKeys = new Set(legacyPromptOrder
        .filter((target): target is Extract<FastFillLegacyPromptTarget, { kind: 'field' }> => (
          target.kind === 'field'
        ))
        .map(({ key }) => key));
      legacyPromptOrder.push(...(settings?.targetFieldKeys ?? [])
        .filter((key) => !orderedKeys.has(key))
        .map((key) => ({ kind: 'field' as const, key })));

      return {
        id: sourceKey,
        sourceKey,
        targetFieldKeys,
        ...(unresolvedLegacyTargetDataNames.length > 0
          ? { legacyTargetDataNames: unresolvedLegacyTargetDataNames }
          : {}),
        ...(legacyPromptOrder.length > 0 ? { legacyPromptOrder } : {}),
        type: settings?.type ?? 'generic',
        isExisting: true,
        existingSourceKey: sourceKey,
      };
    })
    .filter((configuration): configuration is FastFillConfiguration => configuration !== null);

  return {
    audioFastFillEnabled: window.FormBuilder?.model?.get?.('fastfill_audio_enabled') === true,
    configurations,
  };
};

export const readPersistedFastFillSettings = (): FastFillSettingsDraft | undefined => {
  try {
    const rawModels = window.FormBuilder?.model?.allElements?.();
    if (!Array.isArray(rawModels)) return undefined;

    const elementModels = rawModels.filter(isLegacyElementModel);
    if (elementModels.length !== rawModels.length) return undefined;

    const photoFieldKeys = new Set<string>();
    for (const model of elementModels) {
      if (readString(model.get?.('type')) === PHOTO_FIELD_TYPE) {
        const sourceKey = readString(model.get?.('key'));
        if (!sourceKey || photoFieldKeys.has(sourceKey)) return undefined;
        photoFieldKeys.add(sourceKey);

        const settings = model.get?.('fastfill_settings');
        if (settings !== undefined && !isValidPersistedFastFillSettings(settings)) {
          return undefined;
        }
      }
    }

    return readFastFillSettings();
  } catch {
    return undefined;
  }
};

export const readFastFillEnvironment = (
  fastfillEnabled = true,
  photoPlanEnabled = true,
): FastFillSettingsEnvironment => {
  const allFields = models()
    .map(fieldFromModel)
    .filter((field): field is FastFillField => field !== null);
  const targetFields = allFields.filter((field) => !TARGET_FIELD_BLACKLIST.has(field.type));
  const audioFastFillControl = document.getElementById('fastfill_audio_enabled');

  return {
    sourceFields: allFields.filter((field) => field.type === PHOTO_FIELD_TYPE),
    targetFields: targetFields.filter((field) => !field.disabled),
    inactiveTargetFields: targetFields.filter((field) => field.disabled),
    fastfillEnabled,
    photoPlanEnabled,
    canManageSubscription: hiddenFieldIsTrue('form_can_manage_subscription'),
    canUpdateOrganization: hiddenFieldIsTrue('form_can_update_organization'),
    audioFastFillVisible: audioFastFillControl !== null,
    audioFastFillDisabled: audioFastFillControl?.hasAttribute('disabled') ?? false,
  };
};

export const targetSelectionForConfiguration = (
  draft: FastFillSettingsDraft,
  configurationId?: string,
): FastFillTargetSelection => {
  const configuration = draft.configurations.find(({ id }) => id === configurationId);

  return {
    configurationId: configuration?.id ?? configurationId ?? '',
    selectedTargetFieldKeys: [...(configuration?.targetFieldKeys ?? [])],
    ...(configuration?.legacyTargetDataNames
      ? { selectedLegacyTargetDataNames: [...configuration.legacyTargetDataNames] }
      : {}),
    ...(configuration?.legacyPromptOrder
      ? { legacyPromptOrder: configuration.legacyPromptOrder.map((target) => ({ ...target })) }
      : {}),
  };
};

export const targetFieldsEnvironmentForConfiguration = (
  draft: FastFillSettingsDraft,
  environment: FastFillSettingsEnvironment,
  configurationId?: string,
): FastFillTargetFieldsEnvironment => {
  const configuration = draft.configurations.find(({ id }) => id === configurationId);
  const selectedTargetFieldKeys = configuration?.targetFieldKeys ?? [];
  const sourceField = environment.sourceFields.find(({ key }) => key === configuration?.sourceKey);
  const eligibleTargetFields = sourceField
    ? environment.targetFields.filter(
      ({ repeatableContainerKey }) => repeatableContainerKey === sourceField.repeatableContainerKey,
    )
    : [];
  const selectedTargetFieldKeySet = new Set(selectedTargetFieldKeys);
  const inactiveSelectedTargetFields = sourceField
    ? environment.inactiveTargetFields.filter(
      ({ key, repeatableContainerKey }) => (
        selectedTargetFieldKeySet.has(key)
        && repeatableContainerKey === sourceField.repeatableContainerKey
      ),
    )
    : [];
  const resolvedTargetFieldKeys = new Set([
    ...eligibleTargetFields,
    ...inactiveSelectedTargetFields,
  ].map(({ key }) => key));

  return {
    sourceField,
    eligibleTargetFields,
    inactiveSelectedTargetFields,
    unresolvedTargetFieldKeys: selectedTargetFieldKeys.filter(
      (key) => !resolvedTargetFieldKeys.has(key),
    ),
    ...(configuration?.legacyTargetDataNames
      ? { unresolvedLegacyTargetDataNames: [...configuration.legacyTargetDataNames] }
      : {}),
  };
};

export const cloneFastFillSettings = (draft: FastFillSettingsDraft): FastFillSettingsDraft => ({
  audioFastFillEnabled: draft.audioFastFillEnabled,
  configurations: draft.configurations.map((configuration) => ({
    ...configuration,
    targetFieldKeys: [...configuration.targetFieldKeys],
    ...(configuration.legacyTargetDataNames
      ? { legacyTargetDataNames: [...configuration.legacyTargetDataNames] }
      : {}),
    ...(configuration.legacyPromptOrder
      ? { legacyPromptOrder: configuration.legacyPromptOrder.map((target) => ({ ...target })) }
      : {}),
  })),
});

export const fastFillSettingsAreDirty = (
  initialData: FastFillSettingsDraft,
  draft: FastFillSettingsDraft,
): boolean => JSON.stringify(initialData) !== JSON.stringify(draft);

const hasConfiguredFastFillSettings = (draft: FastFillSettingsDraft): boolean => (
  draft.configurations.length > 0
  && draft.configurations.every(
    (configuration) => configuration.sourceKey && (
      configuration.targetFieldKeys.length > 0
      || (configuration.legacyTargetDataNames?.length ?? 0) > 0
    ),
  )
);

export const normalizeFastFillSettings = (
  draft: FastFillSettingsDraft,
): FastFillSettingsDraft => ({
  audioFastFillEnabled: draft.audioFastFillEnabled,
  configurations: draft.configurations
    .map((configuration) => ({
      ...configuration,
      targetFieldKeys: uniqueNonEmptyStrings(configuration.targetFieldKeys),
      ...(configuration.legacyTargetDataNames
        ? { legacyTargetDataNames: [...configuration.legacyTargetDataNames] }
        : {}),
      ...(configuration.legacyPromptOrder
        ? { legacyPromptOrder: configuration.legacyPromptOrder.map((target) => ({ ...target })) }
        : {}),
      type: isSavedType(configuration.type) ? configuration.type : 'generic',
    }))
    .filter((configuration) => configuration.sourceKey && (
      configuration.targetFieldKeys.length > 0
      || (configuration.legacyTargetDataNames?.length ?? 0) > 0
    )),
});

export const hasSubmittableFastFillSettings = (
  initialData: FastFillSettingsDraft,
  draft: FastFillSettingsDraft,
): boolean => (
  hasConfiguredFastFillSettings(draft)
  || JSON.stringify(normalizeFastFillSettings(initialData))
    !== JSON.stringify(normalizeFastFillSettings(draft))
);

export const sourceFieldOptions = (
  draft: FastFillSettingsDraft,
  environment: FastFillSettingsEnvironment,
  activeConfigurationId?: string,
): SettingsOption[] => {
  const claimedKeys = new Set(
    draft.configurations
      .filter(({ id }) => id !== activeConfigurationId)
      .map(({ sourceKey }) => sourceKey)
      .filter(Boolean),
  );

  return environment.sourceFields.map((field) => ({
    value: field.key,
    label: `${field.label} (${field.dataName})`,
    ...(claimedKeys.has(field.key) ? { disabled: true } : {}),
  }));
};

export const commitFastFillSettings = (draft: FastFillSettingsDraft): FastFillSettingsDraft => {
  const normalized = normalizeFastFillSettings(draft);
  const audioFastFillControl = document.getElementById('fastfill_audio_enabled');
  const elementModels = models();
  const fieldDataNameByKey = new Map(
    elementModels
      .map(fieldFromModel)
      .filter((field): field is FastFillField => field !== null)
      .map(({ dataName, key }) => [key, dataName]),
  );
  const configurationEntries: Array<[string, PersistedFastFillSettings]> = normalized.configurations
    .filter(({ targetFieldKeys }) => targetFieldKeys.length > 0)
    .map((configuration) => [
      configuration.sourceKey,
      {
        target_fields: [...configuration.targetFieldKeys],
        type: configuration.type,
      },
    ]);
  const configurationBySourceKey = new Map(configurationEntries);

  elementModels
    .filter((model) => readString(model.get?.('type')) === PHOTO_FIELD_TYPE)
    .forEach((model) => {
      const sourceKey = readString(model.get?.('key'));
      const configuration = configurationBySourceKey.get(sourceKey);
      const persistedSettings = model.get?.('fastfill_settings');
      const draftConfiguration = normalized.configurations.find(
        (candidate) => candidate.sourceKey === sourceKey,
      );
      if (configuration && draftConfiguration) {
        model.set?.({ fastfill_settings: configuration });
      } else if (
        isValidPersistedFastFillSettings(persistedSettings)
        || hasNoNormalizedTargets(persistedSettings)
      ) {
        if (typeof model.unset === 'function') {
          model.unset('fastfill_settings');
        } else {
          model.set?.({ fastfill_settings: undefined });
        }
      }

      const selectedTargetFieldKeys = new Set(draftConfiguration?.targetFieldKeys ?? []);
      const selectedLegacyTargetDataNames = draftConfiguration?.legacyTargetDataNames ?? [];
      const selectedLegacyTargetDataNameSet = new Set(selectedLegacyTargetDataNames);
      const legacyPromptOrder = draftConfiguration?.legacyPromptOrder ?? [];
      const orderedPromptTargetDataNames = legacyPromptOrder.flatMap((target) => {
        if (target.kind === 'field') {
          if (!selectedTargetFieldKeys.has(target.key)) return [];

          const dataName = fieldDataNameByKey.get(target.key);
          return dataName ? [dataName] : [];
        }

        return selectedLegacyTargetDataNameSet.has(target.dataName) ? [target.dataName] : [];
      });
      const orderedPromptTargetFieldKeys = new Set(legacyPromptOrder
        .filter((target): target is Extract<FastFillLegacyPromptTarget, { kind: 'field' }> => (
          target.kind === 'field' && selectedTargetFieldKeys.has(target.key)
        ))
        .map(({ key }) => key));
      const orderedPromptLegacyTargetDataNames = new Set(legacyPromptOrder
        .filter((target): target is Extract<FastFillLegacyPromptTarget, { kind: 'unresolved' }> => (
          target.kind === 'unresolved' && selectedLegacyTargetDataNameSet.has(target.dataName)
        ))
        .map(({ dataName }) => dataName));
      const promptTargetDataNames = [
        ...orderedPromptTargetDataNames,
        ...(draftConfiguration?.targetFieldKeys ?? [])
          .filter((key) => !orderedPromptTargetFieldKeys.has(key))
          .map((key) => fieldDataNameByKey.get(key))
          .filter((dataName): dataName is string => dataName !== undefined),
        ...selectedLegacyTargetDataNames.filter(
          (dataName) => !orderedPromptLegacyTargetDataNames.has(dataName),
        ),
      ];
      model.set?.({ ai_prompt: promptTargetDataNames.join('\n') });
    });

  if (audioFastFillControl && !audioFastFillControl.hasAttribute('disabled')) {
    window.FormBuilder?.model?.set?.({
      fastfill_audio_enabled: normalized.audioFastFillEnabled,
    });
  }

  return normalized;
};
